import BaseStore from 'customer-flows/stores/base-store'
import { tracked } from '@glimmer/tracking'
import { context } from 'customer-flows/utils/core'
import { findSeatBlock, getAllSeatBlocks } from 'customer-flows/utils/seat-maps/seat-block'
import type { BlockDefinition } from '@peekops/peek-extensions-framework/fe-apps/seat-map-renderer/types/seat-map'

export interface SeatTicketOption {
  id: string
  name: string
  priceLabel: string | null
}

export interface SelectedSeat {
  blockId: string
  rowName: string
  seatName: string
  ticketId: string
  ticketOptions: SeatTicketOption[]
}

// map id -> block id -> ticket (resource option) id
export type SeatSelections = Record<string, Record<string, string>>

export default class SeatMapsStoreClass extends BaseStore {
  @context('tickets') whiteListedTickets: TicketsStore['whiteListedTickets']
  @context('tickets') isTicketAvailable: TicketsStore['isTicketAvailable']
  @context('tickets') getLowestPriceForTicket: TicketsStore['getLowestPriceForTicket']
  @context('program/booking-flow') hideDateTime: BookingFlowStore['hideDateTime']

  // replaced on every change (never mutated) so it stays reactive and serializable
  @tracked selections: SeatSelections = {}

  get hasSelectedSeats(): boolean {
    return Object.values(this.selections).some((seats) => Object.keys(seats).length > 0)
  }

  getSelectedBlockIds = (seatMapId: string): string[] => {
    return Object.keys(this.selections[seatMapId] ?? {})
  }

  hasSelectedSeatsFor = (seatMapId: string): boolean => {
    return this.getSelectedBlockIds(seatMapId).length > 0
  }

  // zone.resourceOptionIds are ticket ids; blocks without a zone allow every whitelisted ticket
  getAllowedTicketsForBlock = (block: BlockDefinition): Ticket[] => {
    const allowedTicketIds = block.zone?.resourceOptionIds

    return this.whiteListedTickets
      .filter((ticket) => !allowedTicketIds?.length || allowedTicketIds.includes(ticket.id))
      .filter((ticket) => this.isTicketAvailable(ticket.id))
  }

  getTicketOptionsForBlock = (block: BlockDefinition): SeatTicketOption[] => {
    return this.getAllowedTicketsForBlock(block).map((ticket) => ({
      id: ticket.id,
      name: ticket.name,
      priceLabel: this.hideDateTime ? null : (this.getLowestPriceForTicket(ticket.id)?.formatted ?? null)
    }))
  }

  getSelectableBlocks = (seatMap: SeatMapEntry): BlockDefinition[] => {
    const interactiveStatuses = seatMap.seatStates.filter((state) => state.interactive).map((state) => state.key)

    return getAllSeatBlocks(seatMap.mapData).filter((block) => interactiveStatuses.includes(block.availability.status))
  }

  getAvailableSeatCount = (seatMap: SeatMapEntry): number => {
    return this.getSelectableBlocks(seatMap).length
  }

  // prices belong to ticket types, zones only narrow which tickets a seat allows
  getLowestPriceForSeatMap = (seatMap: SeatMapEntry): Money | null => {
    if (this.hideDateTime) return null

    const ticketIds = new Set(
      this.getSelectableBlocks(seatMap).flatMap((block) => this.getAllowedTicketsForBlock(block).map((ticket) => ticket.id))
    )

    return [...ticketIds].reduce<Money | null>((lowestPrice, ticketId) => {
      const price = this.getLowestPriceForTicket(ticketId)
      const amount = parseFloat(price?.amount ?? '')
      if (!price || isNaN(amount)) return lowestPrice

      return !lowestPrice || amount < parseFloat(lowestPrice.amount) ? price : lowestPrice
    }, null)
  }

  getSelectedSeats = (seatMap: SeatMapEntry): SelectedSeat[] => {
    return Object.entries(this.selections[seatMap.id] ?? {}).flatMap(([blockId, ticketId]) => {
      const match = findSeatBlock(seatMap.mapData, blockId)
      if (!match) return []

      return [{
        blockId,
        rowName: match.group.name,
        seatName: match.block.name ?? match.block.id,
        ticketId,
        ticketOptions: this.getTicketOptionsForBlock(match.block)
      }]
    })
  }
}

declare global {
  export type SeatMapsStore = SeatMapsStoreClass

  export interface StoreRegistry {
    'seat-maps': SeatMapsStoreClass
  }
}
